import {
	removeObjectById, restoreObject,
	setObjectPositionSize, applyStylePatch,
	setCornerResizeInStore, setWrapWidthInStore, setSoftWrapPositionsInStore, setTextContentInStore,
	setImageSrcInStore, setImageUploadInStore, setObjectFieldById, setFieldWidthInStore,
	setBindingById, setImageSourceTypeInStore, restoreImageSourceInStore, moveChildToIndexInStore,
	setTextSourceTypeInStore, setDetachedInStore,
} from 'Shared/posters/project/objects';
import { findObjectById, afterRender } from '../canvas/utils';
import type { Command, RuntimeObject, RuntimeImage, RuntimeText, RuntimeContainer, UploadedImageData, Box, Dimensions } from '../types';
import type { Style, StyleValue, Position, ContainerLayout, Crop } from './posterSchema';
import type { ImageImpl } from '../canvas/images/ImageImpl';
import type { ObjectContext } from './objects';
import type { ObjectType } from 'Shared/posters/project/objects';
import type { SelectionContext } from '../canvas/selection';
import type { ImplRegistry } from '../canvas/interactions/ObjectImpl';
import { defaultSeedBox } from 'Shared/posters/canvas/interactions/ObjectImpl';
import type { CanvasContext } from '../canvas/canvas';


export function addObjectCmd(doAddObject: (type: ObjectType, isField?: boolean) => string, objCtx:ObjectContext, selCtx:SelectionContext, type: ObjectType, isField?: boolean): Command {
	let addedId!: string, snapshot: RuntimeObject | undefined, parentId!: string | null
	// Adding into a container can widen it (see widenContainerForChild in objects.ts), so undo/redo
	// must restore/replay the target container's geometry, not just add/remove the child. The target
	// container is always the added child's parent (parentId). before === after when no widen happened,
	// so these become harmless no-ops on the common path.
	let ctnrGeom: { id: string; before: Box; after: Box } | undefined
	return {
		label: `add ${isField ? 'field ' : ''}${type}`,
		execute() {
			if (snapshot) {
				restoreObject(objCtx, snapshot, parentId)
				if (ctnrGeom) setObjectPositionSize(objCtx, ctnrGeom.id, ctnrGeom.after.pos, ctnrGeom.after.dims)
				selCtx.select(addedId)
			} else {
				const selId  = selCtx.selected()
				const target = selId ? findObjectById(objCtx.objects(), selId) as RuntimeContainer | null : null
				const before = target?.type === 'container' ? containerBox(target, selId ? selCtx.impls[selId]?.getSize?.() : undefined) : undefined
				addedId  = doAddObject(type, isField)
				parentId = objCtx.findParentOf(addedId)
				snapshot = JSON.parse(JSON.stringify(findObjectById(objCtx.objects(), addedId))) as RuntimeObject
				if (selId && before) {
					const after = containerBox(findObjectById(objCtx.objects(), selId) as RuntimeContainer, selCtx.impls[selId]?.getSize?.())
					ctnrGeom = { id: selId, before, after }
				}
			}
		},
		undo() {
			removeObjectById(objCtx, addedId)
			if (ctnrGeom) setObjectPositionSize(objCtx, ctnrGeom.id, ctnrGeom.before.pos, ctnrGeom.before.dims)
			selCtx.deselect()
		},
	}
}

// A container's stored size/position are both optional (an auto-sizing container has neither), but the
// add/undo geometry restore needs a concrete Box, so absent axes fall back to the live measured size
// (getSize) and to 0/origin. Widen only writes size.width, so the height fallback matters even here.
function containerBox(c: RuntimeContainer, live: Dimensions | undefined): Box {
	return {
		pos:  { x: c.position?.x ?? 0, y: c.position?.y ?? 0 },
		dims: {
			width:  c.size?.width  ?? live?.width  ?? 0,
			height: c.size?.height ?? live?.height ?? 0,
		},
	}
}

export function removeObjectCmd(objCtx:ObjectContext, selCtx:SelectionContext, id: string): Command {
	let snapshot!: RuntimeObject, parentId!: string | null
	return {
		label: `remove ${id}`,
		execute() {
			parentId = objCtx.findParentOf(id)
			snapshot = JSON.parse(JSON.stringify(findObjectById(objCtx.objects(), id))) as RuntimeObject
			removeObjectById(objCtx, id)
			selCtx.deselect()
		},
		undo() {
			restoreObject(objCtx, snapshot, parentId)
			selCtx.select(id)
		},
	}
}

// before/after are design box positions (what gets persisted). The live element follows on its own:
// each object's impl factory adopts a design-geometry change into its live box (adoptDesignGeometry),
// re-rendering through livePosition so a hugging field's on-screen (ink) box lands where the design
// anchors it — identity for every other object, whose stored and rendered positions coincide.
//
// detachedSlot is the linked-template slot this move detached the leaf from (null whenever the move
// detached nothing — every design-mode move, and any user-mode move of an object that was already
// detached or never linked). It moves with the position because the two are one edit: undoing the
// move has to put the leaf back under the template's layout, or it would sit at its old position yet
// never reflow with the record again.
export function moveObjectCmd(objCtx:ObjectContext, id: string, before: Position, after: Position, detachedSlot: Dimensions | null): Command {
	return {
		label: `move ${id} (${before.x},${before.y})→(${after.x},${after.y})`,
		mergeKey: `move_${id}`,
		execute() {
			setObjectPositionSize(objCtx, id, after, undefined)
			if (detachedSlot) setDetachedInStore(objCtx, id, detachedSlot)
		},
		undo() {
			setObjectPositionSize(objCtx, id, before, undefined)
			if (detachedSlot) setDetachedInStore(objCtx, id, null)
		},
	}
}

// detachedSlot per entry, for the same reason as moveObjectCmd's: the leaves a multi-drag detached
// from the linked template have to go back onto it when the move is undone.
export interface MultiMoveEntry { id: string; before: Position; after: Position; detachedSlot: Dimensions | null }

export function moveMultipleObjectsCmd(objCtx: ObjectContext, moves: MultiMoveEntry[]): Command {
	const ids = moves.map(m => m.id).join(',')
	return {
		label: `move [${ids}]`,
		execute() {
			for (const { id, after, detachedSlot } of moves) {
				setObjectPositionSize(objCtx, id, after, undefined)
				if (detachedSlot) setDetachedInStore(objCtx, id, detachedSlot)
			}
		},
		undo() {
			for (const { id, before, detachedSlot } of moves) {
				setObjectPositionSize(objCtx, id, before, undefined)
				if (detachedSlot) setDetachedInStore(objCtx, id, null)
			}
		},
	}
}

export function resizeObjectCmd(objCtx:ObjectContext, id: string, before: Box, after: Box): Command {
	return {
		label: `resize ${id} ${before.dims.width}×${before.dims.height}→${after.dims.width}×${after.dims.height}`,
		mergeKey: `resize_${id}`,
		execute() { setObjectPositionSize(objCtx, id, after.pos, after.dims) },
		undo()    { setObjectPositionSize(objCtx, id, before.pos, before.dims) },
	}
}

export function updateStyleCmd(objCtx:ObjectContext, id: string, patch: Style): Command {
	const obj = findObjectById(objCtx.objects(), id)
	const styledObj = (obj ?? null) as { style?: Style } | null
	const prevPatch: Record<string, StyleValue | undefined> = {}
	for (const k of Object.keys(patch)) prevPatch[k] = styledObj?.style?.[k]
	const keys = Object.keys(patch).sort().join(',')
	return {
		label: `style ${id} [${keys}]`,
		mergeKey: `style_${id}_${keys}`,
		execute() { applyStylePatch(objCtx, id, patch) },
		undo()    { applyStylePatch(objCtx, id, prevPatch as Style) },
	}
}

// Like updateStyleCmd, but also clears any theme-variable binding on each patched key.
// Used when a bound property is edited in a user-facing context: the binding supplied the
// property's default value, and editing now takes it over (so the binding's override must
// stop applying). Patched keys that aren't bound just have no binding to clear. Undo
// restores both the previous style values and the previous bindings.
export function updateStyleUnbindCmd(objCtx:ObjectContext, id: string, patch: Style): Command {
	const obj = findObjectById(objCtx.objects(), id)
	const styledObj = (obj ?? null) as { style?: Style } | null
	const bindings = (obj as { bindings?: Record<string, string> } | null)?.bindings
	const keys = Object.keys(patch)
	const prevPatch: Record<string, StyleValue | undefined> = {}
	const prevBindings: Record<string, string | undefined> = {}
	for (const k of keys) {
		prevPatch[k]    = styledObj?.style?.[k]
		prevBindings[k] = bindings?.[k]
	}
	const keyStr = keys.sort().join(',')
	return {
		label: `style ${id} [${keyStr}] (unbind)`,
		mergeKey: `styleUnbind_${id}_${keyStr}`,
		execute() {
			applyStylePatch(objCtx, id, patch)
			for (const k of keys) setBindingById(objCtx, id, k, undefined)
		},
		undo() {
			applyStylePatch(objCtx, id, prevPatch as Style)
			for (const k of keys) setBindingById(objCtx, id, k, prevBindings[k])
		},
	}
}

// Corner-resize commits font size + wrap width every frame while dragging; left/top stay at the
// gesture-start value on those frames (so the merge keeps a correct gesture-start undo) and are
// only moved by the final commit once the box has settled (refreshOnExecute, which re-fits the
// display so redo reflects the move). Position is committed via setObjectPositionSize only — never
// a live geometry write — so the per-frame commits don't fight the live drag's own setPos.
export function cornerResizeCmd(
	objCtx:ObjectContext, impls:ImplRegistry, id: string,
	prevFontSize: number, prevSizeWidth: number | null | undefined, prevPos: Position,
	newFontSize: number, newSizeWidth: number | null, newPos: Position,
	refreshOnExecute = false,
): Command {
	return {
		label: `corner-resize ${id} fontSize ${prevFontSize}→${newFontSize}`,
		mergeKey: `cornerResize_${id}`,
		execute() {
			setCornerResizeInStore(objCtx, id, newFontSize)
			setWrapWidthInStore(objCtx, id, newSizeWidth)
			setObjectPositionSize(objCtx, id, newPos, undefined)
			if (refreshOnExecute) afterRender(() => impls[id]?.refreshSize?.())
		},
		undo() {
			setCornerResizeInStore(objCtx, id, prevFontSize)
			setWrapWidthInStore(objCtx, id, prevSizeWidth ?? null)
			setObjectPositionSize(objCtx, id, prevPos, undefined)
			afterRender(() => impls[id]?.refreshSize?.())
		},
	}
}

// A field-width resize moves the left edge and sets the width; the field's top and height are owned by
// its content fit, so they carry over from the live box unchanged.
function widthResizedBox(live: Box, left: number, width: number): Box {
	return { pos: { x: left, y: live.pos.y }, dims: { width, height: live.dims.height } }
}

// One of the two commands that still writes the live box itself (see moveObjectCmd for why the rest
// don't need to): the box it wants isn't a function of the design geometry, since a field's height is
// owned by its content fit and carries over from the live box. The adopt that the store write triggers
// would instead re-derive the height from the seed, so this write lands after it and wins.
export function fieldWidthResizeCmd(objCtx:ObjectContext, impls:ImplRegistry, id: string, prevLeft: number, prevWidth: number | null, newLeft: number, newWidth: number | null): Command {
	return {
		label: `field-width ${id} w:${prevWidth}→${newWidth}`,
		mergeKey: `fieldWidthResize_${id}`,
		execute() {
			const impl = impls[id]
			setFieldWidthInStore(objCtx, id, newLeft, newWidth)
			if (impl) impl.writeGeometry(widthResizedBox(impl.box, newLeft, newWidth ?? impl.box.dims.width))
		},
		undo() {
			const impl = impls[id]
			setFieldWidthInStore(objCtx, id, prevLeft, prevWidth)
			if (impl) impl.writeGeometry(widthResizedBox(impl.box, prevLeft, prevWidth ?? defaultSeedBox.dims.width))
		},
	}
}

// Plain text's wrap commit: a side-handle drag (or a post-edit reflow) re-derives the soft wrap
// positions for the target width and commits them with the settled position. The wrap width itself
// is never stored — the box hugs the wrapped lines on the next re-fit (refreshSize). Commits once,
// when the gesture ends; the live drag uses a positions override, not this command.
export function setSoftWrapsCmd(
	objCtx:ObjectContext, impls:ImplRegistry, id: string,
	prevWraps: number[], prevPos: Position,
	newWraps: number[], newPos: Position,
): Command {
	return {
		label: `soft-wrap ${id} ${prevWraps.length}→${newWraps.length} breaks`,
		mergeKey: `softWrap_${id}`,
		execute() {
			setSoftWrapPositionsInStore(objCtx, id, newWraps)
			setObjectPositionSize(objCtx, id, newPos, undefined)
			afterRender(() => impls[id]?.refreshSize?.())
		},
		undo() {
			setSoftWrapPositionsInStore(objCtx, id, prevWraps)
			setObjectPositionSize(objCtx, id, prevPos, undefined)
			afterRender(() => impls[id]?.refreshSize?.())
		},
	}
}

// A user-mode field commits its *reserved box* when a width drag ends — the (frozen) render font and
// the wrap width — so a fresh re-fit on reload reproduces the reflowed box rather than shrinking the
// font back to the original single reserved line. The reserved height is not persisted (importExport
// strips size.height for fields); it is re-derived by wrapping the content at the stored font and
// width on reload. Plain text needs none of this: its font lives in the store and its box simply hugs
// the measured text on reload, so it commits soft-wrap positions (setSoftWrapsCmd above) instead.
// Re-fits the display (refreshSize) so redo/undo settle the measured box.
type Reserve = { fontSize: number, box: Box }

// The reserve as it stood in the store before the drag. A field that has never been width-resized has
// no stored font, and one reloaded from a save has had its height stripped, so undo has to restore the
// *absence* of those — hence the nullable font and the partial dims, which the full Reserve never has.
type PriorReserve = { fontSize: number | null, box: { pos: Position, dims: Partial<Dimensions> } }

export function fieldReserveCmd(
	objCtx:ObjectContext, impls:ImplRegistry, id: string,
	prev: PriorReserve,
	next: Reserve,
): Command {
	return {
		label: `field-reserve ${id} ${next.box.dims.width}×${next.box.dims.height} @${next.fontSize}`,
		mergeKey: `widthResize_${id}`,
		execute() {
			setCornerResizeInStore(objCtx, id, next.fontSize)
			setObjectPositionSize(objCtx, id, next.box.pos, next.box.dims)
			afterRender(() => impls[id]?.refreshSize?.())
		},
		undo() {
			if (prev.fontSize !== null) setCornerResizeInStore(objCtx, id, prev.fontSize)
			setObjectPositionSize(objCtx, id, prev.box.pos, prev.box.dims)
			afterRender(() => impls[id]?.refreshSize?.())
		},
	}
}

const truncate = (s: string | null | undefined, n = 30): string =>
	s == null ? 'null' : (s.length > n ? s.slice(0, n) + '…' : s)

export function setTextContentCmd(objCtx:ObjectContext, impls:ImplRegistry, id: string, before: string, after: string): Command {
	return {
		label: `text-content ${id} "${truncate(before)}"→"${truncate(after)}"`,
		mergeKey: `textContent_${id}`,
		execute() {
			setTextContentInStore(objCtx, id, after)
			afterRender(() => impls[id]?.refreshSize?.())
		},
		undo() {
			setTextContentInStore(objCtx, id, before)
			afterRender(() => impls[id]?.refreshSize?.())
		},
	}
}

export function setObjectFieldCmd(objCtx:ObjectContext, id: string, key: string, before: unknown, after: unknown): Command {
	return {
		label: `field ${id}.${key} ${before}→${after}`,
		mergeKey: `field_${id}_${key}`,
		execute() { setObjectFieldById(objCtx, id, key, after) },
		undo()    { setObjectFieldById(objCtx, id, key, before) },
	}
}

// Records a vertical drag-reorder of a flex-container child. The live drag already mutated
// the store to the final order, so execute() (idempotent) just re-asserts toIndex; undo()
// puts the child back at its original index.
export function reorderChildCmd(objCtx:ObjectContext, parentId: string, childId: string, fromIndex: number, toIndex: number): Command {
	return {
		label: `reorder ${childId} ${fromIndex}→${toIndex}`,
		execute() { moveChildToIndexInStore(objCtx, parentId, childId, toIndex) },
		undo()    { moveChildToIndexInStore(objCtx, parentId, childId, fromIndex) },
	}
}

// Changes a container's layout. When switching to 'absolute' from a flex layout, the
// children's positions were owned by the container (computed live as a layoutPos override,
// never written to each child's stored position). Dropping to absolute would snap them back
// to their stale stored positions, so we first bake each child's currently-displayed position
// (read from its live bbox, which folds in the flex override) into its stored position. The
// before/after positions are snapshotted on first execute so undo/redo stay exact.
export function setContainerLayoutCmd(
	objCtx:ObjectContext, impls:ImplRegistry,
	id: string, before: ContainerLayout, after: ContainerLayout,
): Command {
	let baked: MultiMoveEntry[] | null = null

	const captureBaked = (): MultiMoveEntry[] => {
		const container = findObjectById(objCtx.objects(), id) as RuntimeContainer | null
		return (container?.objects ?? []).map(child => {
			const bbox   = impls[child.id]?.getBBox?.()
			const stored = child.position ?? { x: 0, y: 0 }
			return {
				id:     child.id,
				before: { x: stored.x ?? 0, y: stored.y ?? 0 },
				after:  { x: Math.round(bbox?.pos.x ?? stored.x ?? 0), y: Math.round(bbox?.pos.y ?? stored.y ?? 0) },
				// A design-mode layout change: containers and the template link never meet.
				detachedSlot: null,
			}
		})
	}

	return {
		label: `layout ${id} ${before}→${after}`,
		execute() {
			// Bake before flipping the field: once layout is 'absolute' the override is gone and
			// the bbox would read the stale stored position instead of the displayed one.
			if (after === 'absolute' && before !== 'absolute') {
				if (!baked) baked = captureBaked()
				for (const { id: childId, after: pos } of baked)
					setObjectPositionSize(objCtx, childId, pos, undefined)
			}
			setObjectFieldById(objCtx, id, 'layout', after)
		},
		undo() {
			setObjectFieldById(objCtx, id, 'layout', before)
			if (baked) {
				for (const { id: childId, before: pos } of baked)
					setObjectPositionSize(objCtx, childId, pos, undefined)
			}
		},
	}
}

// The other command that still writes the live box itself: the top it computes is an alignment anchor
// in *design* coordinates and is written straight through, where the adopt would re-anchor it through
// livePosition (a different point for a user-mode field, whose live box hugs its content). The write
// lands after the adopt and wins.
// Bundles a field-content edit with an optional alignment-anchored top update into one undo step.
// When prevTop === newTop, behaves like a plain content edit. The box height is not stored — it is
// re-derived from the content on reload (see reservedHeight). Fields store their template string in
// `content` (the same field plain text uses), so the Source toggle never rewrites the text — see TextObject.
export function setTemplateAndResizeCmd(
	objCtx:ObjectContext, impls:ImplRegistry,
	id: string,
	prevTemplate: string, newTemplate: string,
	prevTop: number, newTop: number,
): Command {
	return {
		label: `template ${id} "${truncate(prevTemplate)}"→"${truncate(newTemplate)}"`,
		mergeKey: `template_${id}`,
		execute() {
			const impl = impls[id]
			setObjectFieldById(objCtx, id, 'content', newTemplate)
			if (newTop !== prevTop) {
				setObjectPositionSize(objCtx, id, { y: newTop }, undefined)
				if (impl) impl.writePos({ x: impl.box.pos.x, y: newTop })
			}
		},
		undo() {
			const impl = impls[id]
			setObjectFieldById(objCtx, id, 'content', prevTemplate)
			if (newTop !== prevTop) {
				setObjectPositionSize(objCtx, id, { y: prevTop }, undefined)
				if (impl) impl.writePos({ x: impl.box.pos.x, y: prevTop })
			}
		},
	}
}

export function zOrderCmd(doMoveUp: (id: string) => void, doMoveDown: (id: string) => void, id: string, direction: 'up' | 'down'): Command {
	return {
		label: `z-order ${id} ${direction}`,
		execute() { direction === 'up' ? doMoveUp(id) : doMoveDown(id) },
		undo()    { direction === 'up' ? doMoveDown(id) : doMoveUp(id) },
	}
}

export function setImageSrcCmd(objCtx:ObjectContext, id: string, before: string | null, after: string | null): Command {
	return {
		label: `image-src ${id} ${before ? '(replace)' : '(set)'}`,
		execute() {
			setImageSrcInStore(objCtx, id, after)
		},
		undo() {
			setImageSrcInStore(objCtx, id, before)
		},
	}
}

// Zoom/aspect/pan of the image's crop view. Applied to the live impl signals (which the
// image renderer and the toolbar sliders read); serializeObjs reads the live crop back onto
// the object so AutoSave persists it. The mergeKey collapses a slider drag into one undo step.
export function setImageCropCmd(impl: ImageImpl, before: Required<Crop>, after: Required<Crop>): Command {
	return {
		label:    `image-crop ${impl.id}`,
		mergeKey: `crop_${impl.id}`,
		execute() { impl.applyCrop(after) },
		undo()    { impl.applyCrop(before) },
	}
}

export function setImageUploadCmd(
	objCtx: ObjectContext,
	id: string,
	before: UploadedImageData | null,
	after: UploadedImageData | null
): Command {
	return {
		label: `image-upload ${id} ${before ? '(replace)' : '(set)'}`,
		execute() {
			setImageUploadInStore(objCtx, id, after)
		},
		undo() {
			setImageUploadInStore(objCtx, id, before)
		},
	}
}

// Switches an image's source type between "Field" (record-bound) and "Upload", clearing the
// opposite source. Snapshots the full prior source state so undo restores it exactly.
export function setImageSourceTypeCmd(objCtx:ObjectContext, id: string, isField: boolean): Command {
	let before: { isField?: boolean; image?: UploadedImageData; src?: string | null; field?: string } = {}
	return {
		label: `image-source-type ${id} → ${isField ? 'field' : 'upload'}`,
		execute() {
			const obj = findObjectById(objCtx.objects(), id) as RuntimeImage | null
			if (obj) before = { isField: obj.isField, image: obj.image, src: obj.src ?? null, field: obj.field }
			setImageSourceTypeInStore(objCtx, id, isField)
		},
		undo() { restoreImageSourceInStore(objCtx, id, before) },
	}
}

// Toggle a text object's source between a plain literal and a data-bound template field.
// The text object is keyed on its field flag in Object.tsx, so it remounts and re-picks its
// resize/fit strategy (hugging for plain, reserved box for a field) when this runs. Becoming a
// field also stores `reserveWidth` (see fieldReserveWidthFor) — plain text carries no width, so
// without it the field would fall back to its 200px default and overhang a narrower container.
// Switching back to plain leaves the width where it is; undo restores the exact prior pair.
export function setTextSourceTypeCmd(objCtx:ObjectContext, id: string, isField: boolean, reserveWidth: number): Command {
	let before: { isField: boolean; width: number | null } = { isField: false, width: null }
	return {
		label: `text-source-type ${id} → ${isField ? 'template' : 'plain'}`,
		execute() {
			const obj = findObjectById(objCtx.objects(), id) as RuntimeText | null
			if (obj) before = { isField: !!obj.style?.isField, width: obj.size?.width ?? null }
			setTextSourceTypeInStore(objCtx, id, isField, isField ? reserveWidth : before.width)
		},
		undo() { setTextSourceTypeInStore(objCtx, id, before.isField, before.width) },
	}
}

export function setImageFieldCmd(objCtx:ObjectContext, id: string, before: string, after: string): Command {
	return {
		label: `image-field ${id} "${before}"→"${after}"`,
		execute() { setObjectFieldById(objCtx, id, 'field', after) },
		undo()    { setObjectFieldById(objCtx, id, 'field', before) },
	}
}

export function setBindingCmd(objCtx:ObjectContext, id: string, key: string, before: string | undefined, after: string | undefined): Command {
	return {
		label: `binding ${id}.${key} ${before ?? '(none)'}→${after ?? '(none)'}`,
		mergeKey: `binding_${id}_${key}`,
		execute() { setBindingById(objCtx, id, key, after) },
		undo()    { setBindingById(objCtx, id, key, before) },
	}
}

export function setCanvasBgCmd(canvasCtx: CanvasContext, before: string, after: string): Command {
	return {
		label: `canvas-bg ${before}→${after}`,
		mergeKey: 'canvasBg',
		execute() { canvasCtx.setCanvasBg(after) },
		undo()    { canvasCtx.setCanvasBg(before) },
	}
}

function withBinding(map: Record<string, string>, key: string, value: string | undefined): Record<string, string> {
	const next = { ...map }
	if (value == null || value === '') delete next[key]
	else next[key] = value
	return next
}

// Like setCanvasBgCmd, but also clears any theme-variable binding on the canvas background.
// The bound variable supplied the default background; editing it in a user-facing context
// takes it over. Undo restores both the previous colour and the previous binding.
export function setCanvasBgUnbindCmd(canvasCtx: CanvasContext, before: string, after: string): Command {
	const prevBinding = canvasCtx.canvasBindings().background
	return {
		label: `canvas-bg ${before}→${after} (unbind)`,
		mergeKey: 'canvasBgUnbind',
		execute() {
			canvasCtx.setCanvasBg(after)
			canvasCtx.setCanvasBindings(withBinding(canvasCtx.canvasBindings(), 'background', undefined))
		},
		undo() {
			canvasCtx.setCanvasBg(before)
			canvasCtx.setCanvasBindings(withBinding(canvasCtx.canvasBindings(), 'background', prevBinding))
		},
	}
}

export function setCanvasBindingCmd(canvasCtx: CanvasContext, key: string, before: string | undefined, after: string | undefined): Command {
	return {
		label: `canvas-binding ${key} ${before ?? '(none)'}→${after ?? '(none)'}`,
		mergeKey: `canvasBinding_${key}`,
		execute() { canvasCtx.setCanvasBindings(withBinding(canvasCtx.canvasBindings(), key, after)) },
		undo()    { canvasCtx.setCanvasBindings(withBinding(canvasCtx.canvasBindings(), key, before)) },
	}
}
